import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Alert,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { useCreatePlatformUser } from '@/features/platform-users';
import {
  PLATFORM_ROLE_DESCRIPTION,
  PLATFORM_ROLE_LABEL,
  type CreatePlatformUserPayload,
  type PlatformRole,
} from '@/types/platform-user';
import { ApiError } from '@/lib/api-client';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_REGEX = /^\+[1-9]\d{6,14}$/;

const ROLE_OPTIONS: { value: PlatformRole; label: string }[] = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MODERATOR', label: 'Moderator' },
];

const ROLE_TONE: Record<PlatformRole, string> = {
  SUPER_ADMIN: 'from-danger-500 to-rose-600',
  ADMIN: 'from-brand-500 to-indigo-600',
  MODERATOR: 'from-warning-500 to-amber-600',
};

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
}

function PlatformUserCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreatePlatformUser();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<PlatformRole>('ADMIN');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    else if (name.trim().length < 2) errs.name = 'Min 2 characters';
    else if (name.length > 200) errs.name = 'Max 200 characters';

    if (!email.trim()) errs.email = 'Email is required';
    else if (!EMAIL_REGEX.test(email)) errs.email = 'Enter a valid email address';

    if (phone && !E164_REGEX.test(phone)) {
      errs.phone = 'Enter a valid phone number';
    }

    if (!password) errs.password = 'Password is required';
    else if (password.length < 8) errs.password = 'Min 8 characters';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;

    const payload: CreatePlatformUserPayload = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      role,
      password,
      ...(phone ? { phone } : {}),
    };

    createMutation.mutate(payload, {
      onSuccess: (result) => {
        toast({ title: `${result.name} added`, variant: 'success' });
        navigate(`/platform-users/${result.id}`);
      },
      onError: (err: Error) => {
        if (err instanceof ApiError) {
          if (err.statusCode === 409) {
            setErrors((prev) => ({ ...prev, email: 'This email is already registered' }));
            return;
          }
          if (err.statusCode === 400 && err.details?.length) {
            const next: Record<string, string> = {};
            for (const d of err.details) next[d.field] = d.message;
            setErrors((prev) => ({ ...prev, ...next }));
            return;
          }
        }
        toast({ title: err.message, variant: 'error' });
      },
    });
  }

  // Keep latest handleSubmit in a ref so the keydown listener doesn't need to re-bind
  // on every keystroke — avoids exhaustive-deps churn over form state.
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/platform-users');
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmitRef.current();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <div className="mx-auto max-w-3xl animate-fade-in">
      {/* Gradient hero header — matches List/Detail */}
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-[0_20px_60px_-20px_rgba(67,56,202,0.55)]">
        <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate('/platform-users')}
            className="mt-1 rounded-lg p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Back"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/20">
              <ShieldIcon className="h-3.5 w-3.5" />
              Platform Access
            </div>
            <h1 className="text-2xl font-bold">Add Platform User</h1>
            <p className="mt-1 text-indigo-100">
              Grant cross-tenant platform access to a new staff member.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Identity */}
        <div className="rounded-2xl bg-white/80 p-6 shadow-md backdrop-blur-sm dark:bg-neutral-900/80">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-neutral-900 dark:text-white">
            <span className="h-4 w-1.5 rounded-full bg-gradient-to-b from-brand-500 to-indigo-500" />
            Identity
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Full Name <span className="text-danger-500">*</span>
              </label>
              <Input
                value={name}
                onChange={(v) => {
                  setName(v);
                  if (errors.name) setErrors((e) => ({ ...e, name: '' }));
                }}
                placeholder="e.g. Priya Sharma"
              />
              {errors.name && <p className="mt-1 text-xs text-danger-500">{errors.name}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Email <span className="text-danger-500">*</span>
              </label>
              <Input
                value={email}
                onChange={(v) => {
                  setEmail(v);
                  if (errors.email) setErrors((e) => ({ ...e, email: '' }));
                }}
                placeholder="priya@company.com"
                className="font-mono"
              />
              {errors.email && <p className="mt-1 text-xs text-danger-500">{errors.email}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Phone
              </label>
              <Input
                // no-tenant-dial: Orio360 platform staff, not a tenant user — there is no
                // owning tenant whose country this should follow.
                variant="phone"
                value={phone}
                onChange={(v) => {
                  setPhone(v);
                  if (errors.phone) setErrors((e) => ({ ...e, phone: '' }));
                }}
                placeholder="98765 43210"
                className="font-mono tabular-nums"
              />
              {errors.phone && <p className="mt-1 text-xs text-danger-500">{errors.phone}</p>}
              <p className="mt-1 text-xs text-neutral-400">Optional. Pick the country code, then enter the mobile number.</p>
            </div>
          </div>
        </div>

        {/* Access & Credentials */}
        <div className="rounded-2xl bg-white/80 p-6 shadow-md backdrop-blur-sm dark:bg-neutral-900/80">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-neutral-900 dark:text-white">
            <span className="h-4 w-1.5 rounded-full bg-gradient-to-b from-danger-500 to-rose-500" />
            Access & Credentials
          </h2>

          {/* Role selector — custom card buttons for clarity */}
          <div className="mb-5">
            <label className="mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Platform Role <span className="text-danger-500">*</span>
            </label>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {ROLE_OPTIONS.map((opt) => {
                const selected = role === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRole(opt.value)}
                    aria-pressed={selected}
                    className={`group relative overflow-hidden rounded-xl p-4 text-left transition-all duration-200 ${
                      selected
                        ? 'bg-white shadow-lg ring-2 ring-brand-500 dark:bg-neutral-800'
                        : 'bg-neutral-50 shadow-sm hover:shadow-md dark:bg-neutral-800/50'
                    }`}
                  >
                    <div
                      className={`pointer-events-none absolute -top-6 -right-6 h-16 w-16 rounded-full bg-gradient-to-br ${ROLE_TONE[opt.value]} opacity-20`}
                    />
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <span
                          className={`h-2 w-2 rounded-full bg-gradient-to-br ${ROLE_TONE[opt.value]}`}
                        />
                        <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                          {opt.label}
                        </span>
                      </div>
                      <p className="text-xs leading-snug text-neutral-500 dark:text-neutral-400">
                        {PLATFORM_ROLE_DESCRIPTION[opt.value]}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Initial Password <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((er) => ({ ...er, password: '' }));
                }}
                placeholder="Min 8 characters"
                className="w-full rounded-lg bg-neutral-50 p-2.5 pr-10 font-mono text-sm text-neutral-900 ring-1 ring-inset ring-transparent transition-shadow placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-neutral-800/60 dark:text-white"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-xs text-danger-500">{errors.password}</p>}
            <p className="mt-1 text-xs text-neutral-400">
              Share this password securely with the user. They can change it after first login.
            </p>
          </div>

          <Alert
            variant="info"
            title="Platform-scoped access"
            description={
              <>
                Platform users have no tenant or location scope. The{' '}
                <strong>{PLATFORM_ROLE_LABEL[role]}</strong> role grants cross-tenant visibility into
                administrative tooling. Only Super Admins can create, edit, or deactivate platform users.
              </>
            }
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={() => navigate('/platform-users')}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Saving...' : 'Create Platform User'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PlatformUserCreatePage() {
  return (
    <PageContainer
      title="Add Platform User"
      breadcrumbs={[
        { label: 'Platform', href: '/dashboard' },
        { label: 'Platform Users', href: '/platform-users' },
        { label: 'Add Platform User' },
      ]}
    >
      <ErrorBoundary>
        <PlatformUserCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
